import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
// Note: We are avoiding imports like 'react-icons/bs', 'sweetalert2', 
// and absolute utility paths to prevent compilation errors in this environment.
import { format } from 'date-fns';
// Removed unused import: import { useNavigate } from 'react-router-dom';

const NotificationsList = () => {
    // NOTE: This placeholder URL should be replaced with a correctly injected environment variable in a real build environment.
    const BASE_API_URL = process.env.REACT_APP_WEB_SERVICES_URL || '/api/v1';

    const debounceTimeout = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
    const [notifications, setNotifications] = useState([]);
    
    // State for viewing recipients
    const [showRecipientsModal, setShowRecipientsModal] = useState(false);
    const [currentRecipients, setCurrentRecipients] = useState([]);
    const [currentNotification, setCurrentNotification] = useState(null);
    // Removed recipientLoading state as we no longer make a second API call

    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            getNotifications();
        }, 500);
        return () => clearTimeout(debounceTimeout.current);
    }, [pagination.page]);


    const getNotifications = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${BASE_API_URL}/admin/get-notifications`, {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                },
                withCredentials: true
            });

            if (res.data.status) {
                // The controller now sends recipients directly within 'notifications'
                setNotifications(res.data.notifications || []);
                setPagination(res.data.pagination);
            } else {
                setNotifications([]);
                console.error('Failed to fetch notifications:', res.data.message);
            }
        } catch (error) {
            console.error('Network or server error fetching notifications:', error);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Updated function to show recipients using pre-loaded data from the 'notifications' state.
     * This eliminates the need for a secondary API call.
     */
    const showPreloadedRecipients = (notification) => {
        setCurrentNotification(notification);
        // Recipient data is already attached to the notification object by the controller
        setCurrentRecipients(notification.recipients || []); 
        setShowRecipientsModal(true); 
    };


    // Modal Component for Recipients (defined inline for simplicity)
    const ViewRecipientsModal = () => (
        <Modal show={showRecipientsModal} onHide={() => setShowRecipientsModal(false)} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Recipients for: <span className='text-primary'>{currentNotification?.message}</span></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Removed recipientLoading check */}
                {currentRecipients.length === 0 ? (
                    <p>No recipients found for this notification.</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Role</th>
                                    <th>User Name</th>
                                    <th>Property</th>
                                    
                                </tr>
                            </thead>
                            <tbody>
                                {currentRecipients.map((r, index) => (
                                    <tr key={index}>
                                        <td><span className='badge bg-secondary'>{r.user_role}</span></td>
                                        <td>{r.user_name || 'N/A'}</td>
                                        {/* Display 'Global' if property_name is null/undefined */}
                                        <td>{r.property_name || 'Global'}</td> 
                                        
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowRecipientsModal(false)}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );


    return (
        <div className="container-fluid px-4 mt-4">
            <div className='row mb-3' >
                <h3>View Notification</h3>
            </div>


            <div className="card shadow mt-4">
                <div className="card-header">
                    <h5 className="mb-0">View Notification</h5>
                </div>
                <div className="card-body p-4">
                 
                    {/* Notification Table */}

                    <div className="table-responsive">
                        <table className="table table-bordered table-hover shadow-sm nowrap">
                            <thead className="table-success">
                                <tr>
                                    <th>Sr. #</th>
                                    <th>Date & Time</th>
                                    <th>Message</th>
                                    <th>Recipients</th> {/* Changed column title */}
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                                ) : notifications.length === 0 ? (
                                    <tr><td colSpan="4" className='text-center'>No data found.</td></tr>
                                ) : (
                                    notifications.map((n, i) => (
                                        <tr key={n.id}>
                                            <td>{i + 1 + ((pagination.page - 1) * pagination.limit)}</td> 
                                            <td>{format(new Date(n.created_at), 'MMM d, yyyy h:mm a')}</td>
                                            <td>{n.message}</td>
                                            <td>
                                                <Button 
                                                    variant="info" 
                                                    size="sm" 
                                                    // Use the preloaded data attached to the notification object
                                                    onClick={() => showPreloadedRecipients(n)}
                                                    disabled={isLoading} // Use list loading state instead
                                                >
                                                    View Recipients ({n.recipient_count || 0})
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-end mt-3">
                        <nav>
                            <ul className="pagination">
                                {[...Array(pagination.totalPages)].map((_, i) => (
                                    <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                                            disabled={isLoading}
                                        >
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>

            {/* Recipients Modal */}
            {showRecipientsModal && <ViewRecipientsModal />}
        </div>
    );
};

export default NotificationsList;
